import { useState, useRef, useEffect, useCallback } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import { IconButton } from "@atlaskit/button-next/new";
import { NewCoreIconProps } from "@atlaskit/icon";
import CopyIcon from "@atlaskit/icon/core/copy";
import InformationCircleIcon from "@atlaskit/icon/core/information-circle";
import ThumbsDownIcon from "@atlaskit/icon/core/thumbs-down";
import ThumbsUpIcon from "@atlaskit/icon/core/thumbs-up";
import { SelectedType } from "@atlaskit/tabs/dist/types/types";
import { token } from "@atlaskit/tokens";
import Tooltip from "@atlaskit/tooltip";

import { Tracking } from "@/analytics/atl-analytics/tracking";
import { Link } from "@/libraries/design/components";
import { wacToken } from "@/libraries/design/foundations";
import { useLocalizedHref } from "@/libraries/web-framework";

import { Source } from "./ai-summary";
import { AISummaryTabs } from "./ai-summary-tabs";
import { MarkdownRenderer } from "./renderer/markdown-renderer";

const sourceIconStyle = css({
    "border": `1px solid ${wacToken("color.extended.blue500")}`,
    "borderRadius": token("space.050"),
    "fontWeight": "bold",
    "fontSize": token("space.150"),
    "width": "18px",
    "height": "18px",
    "lineHeight": "15px",
    "display": "inline-block",
    "paddingInline": "1px",
    "textAlign": "center",
    "marginInline": "0px",
    "cursor": "pointer",

    ":hover": {
        backgroundColor: wacToken("color.extended.blue100")
    }
});

const sourceIconTooltipStyle = css({
    "display": "inline",
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "& > div": {
        display: "inline"
    }
});

const SourceIcon = ({
    identifier,
    sources
}: {
    identifier: number;
    sources?: Source[];
}) => {
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const handleClick = () => {
        const sourceIndex = identifier - 1; // Convert 1-based citation to 0-based array index
        if (sources && sources[sourceIndex]?.url) {
            createAnalyticsEvent({
                eventType: "ui",
                action: "clicked",
                actionSubject: "link",
                actionSubjectId: "searchAiSummaryInlineSourceLink",
                source: "searchAiSummary",
                attributes: {
                    sourceIndex: sourceIndex,
                    sourceTitle: sources[sourceIndex].title,
                    sourceUrl: sources[sourceIndex].url
                }
            }).fire();
            window.open(
                sources[sourceIndex].url,
                "_blank",
                "noopener,noreferrer"
            );
        }
    };

    return (
        <div css={sourceIconTooltipStyle}>
            <Tooltip content={sources?.[identifier - 1]?.title}>
                {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
                <span css={sourceIconStyle} onClick={handleClick}>
                    {identifier}
                </span>
            </Tooltip>
        </div>
    );
};

const StatusComponent = (props: { text?: string; sources?: Source[] }) => (
    <SourceIcon
        identifier={parseInt(props.text || "1", 10)}
        sources={props.sources}
    />
);

const fullContent = css({
    maxHeight: "fit-content"
});

const defaultContent = css({
    maxHeight: "136px",
    overflow: "hidden",
    position: "relative"
});

const blurOverlayStyle = css({
    background: `linear-gradient(180deg, transparent 0%, ${token(
        "elevation.surface.overlay",
        "#FFFFFF"
    )} 60%)`,
    width: "100%",
    display: "flex",
    justifyContent: "flex-start",
    position: "absolute",
    insetBlockStart: "85px",
    cursor: "pointer",
    paddingBlockEnd: token("space.050", "4px"),
    paddingBlockStart: token("space.400", "32px"),
    color: token("color.text", "#172B4D")
});

const blurTextStyle = css({
    display: "flex",
    justifyContent: "flex-start",
    paddingInlineEnd: token("space.050", "4px")
});

const aiSummaryFooterStyle = css({
    "display": "flex",
    "justifyContent": "space-between",
    "padding": token("space.050", "4px"),

    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "& span": {
        lineHeight: "21px"
    }
});

const aiSummaryFooterSectionStyle = css({
    display: "flex",
    justifyContent: "flex-start",
    gap: token("space.050", "4px")
});

const disclaimerTextStyle = css({
    color: token("color.text.subtlest"),
    fontSize: "14px"
});

const FeedbackThumpsUpIcon = (
    iconProps: NewCoreIconProps,
    feedbackState: "thumbsUp" | "thumbsDown" | null
) => {
    return (
        <ThumbsUpIcon
            {...iconProps}
            color={
                feedbackState === "thumbsUp"
                    ? token("color.icon.selected")
                    : undefined
            }
        />
    );
};

const FeedbackThumpsDownIcon = (
    iconProps: NewCoreIconProps,
    feedbackState: "thumbsUp" | "thumbsDown" | null
) => {
    return (
        <ThumbsDownIcon
            {...iconProps}
            color={
                feedbackState === "thumbsDown"
                    ? token("color.icon.selected")
                    : undefined
            }
        />
    );
};

export const AISummaryFooter = ({ aiSummary }: { aiSummary: string }) => {
    const intl = useIntl();
    const href = useLocalizedHref("/trust/atlassian-intelligence");
    const { createAnalyticsEvent } = useAnalyticsEvents();

    const aiDisclaimerText = intl.formatMessage({
        id: "ai.summary.answer.footer.disclaimer",
        defaultMessage: "Uses AI. Verify results."
    });

    const handleCopy = async () => {
        try {
            // Remove citation markers like [1], [2], etc. from the text
            const textToCopy = aiSummary.replace(/\[\d+\]/g, "").trim();
            await navigator.clipboard.writeText(textToCopy);
        } catch (err) {
            console.error("Failed to copy text:", err);
        }
    };

    const thumbsUpText = intl.formatMessage({
        id: "ai.summary.answer.footer.thumbsUp",
        defaultMessage: "Good response"
    });
    const thumbsDownText = intl.formatMessage({
        id: "ai.summary.answer.footer.thumbsDown",
        defaultMessage: "Bad response"
    });
    const copyText = intl.formatMessage({
        id: "ai.summary.answer.footer.copy",
        defaultMessage: "Copy"
    });

    const [feedbackState, setFeedbackState] = useState<
        "thumbsUp" | "thumbsDown" | null
    >(null);

    const handleFeedbackClick = (
        event: React.MouseEvent<HTMLButtonElement>,
        state: "thumbsUp" | "thumbsDown"
    ) => {
        event.preventDefault();
        setFeedbackState(state);
        createAnalyticsEvent({
            eventType: "ui",
            action: "clicked",
            actionSubject: "feedbackButton",
            actionSubjectId: "searchAiSummaryFeedbackClicked",
            source: "searchAiSummary",
            attributes: {
                feedback: state
            }
        }).fire();
    };

    useEffect(() => {
        Tracking.autoTrackLinks(true);
    }, []);

    return (
        <div css={aiSummaryFooterStyle}>
            <div css={aiSummaryFooterSectionStyle}>
                <InformationCircleIcon
                    color={token("color.icon.subtlest")}
                    size="medium"
                    label=""
                />
                <Link
                    text={aiDisclaimerText}
                    type="anchor"
                    href={href}
                    ariaLabel={aiDisclaimerText}
                    css={disclaimerTextStyle}
                    anchorProps={{ target: "_blank" }}
                    dataEventProps={{
                        "data-product-key": "search",
                        "data-event": "clicked",
                        "data-event-component": "linkButton",
                        "data-event-container": "searchAiSummary",
                        "data-label-english": "Uses AI"
                    }}
                />
            </div>
            <div css={aiSummaryFooterSectionStyle}>
                <IconButton
                    appearance="subtle"
                    icon={(iconProps) =>
                        FeedbackThumpsUpIcon(iconProps, feedbackState)
                    }
                    label={thumbsUpText}
                    onClick={(event) => handleFeedbackClick(event, "thumbsUp")}
                    isTooltipDisabled={false}
                />
                <IconButton
                    appearance="subtle"
                    icon={(iconProps) =>
                        FeedbackThumpsDownIcon(iconProps, feedbackState)
                    }
                    label={thumbsDownText}
                    onClick={(event) =>
                        handleFeedbackClick(event, "thumbsDown")
                    }
                    isTooltipDisabled={false}
                />
                <IconButton
                    appearance="subtle"
                    icon={CopyIcon}
                    label={copyText}
                    onClick={handleCopy}
                    isTooltipDisabled={false}
                />
            </div>
        </div>
    );
};

const dividerStyle = css({
    borderBlockEnd: `1px solid ${wacToken("color.neutral.light300")}`,
    margin: `${token("space.200")} 0`
});

const markdownContainerStyle = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "& div": {
        fontSize: `${token("font.body")} !important`
    }
});

const sourceStyle = css({
    "padding": `${token("space.100")} 0`,
    "fontWeight": 500,

    "&:first-child": {
        paddingBlockStart: 0
    },

    "&:last-child": {
        paddingBlockEnd: 0
    }
});

type AISummaryAnswerProps = {
    aiSummary: string;
    sources?: Source[];
    showAdditionalSummary: boolean;
    setShowAdditionalSummary: (showAdditionalSummary: boolean) => void;
};

export const AISummaryAnswer = ({
    aiSummary,
    sources,
    showAdditionalSummary: _showAdditionalSummary,
    setShowAdditionalSummary: _setShowAdditionalSummary
}: AISummaryAnswerProps) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [isTruncated, setIsTruncated] = useState(false);
    const contentRef = useRef<HTMLDivElement>(null);
    const intl = useIntl();
    const { createAnalyticsEvent } = useAnalyticsEvents();

    const [selected, setSelected] = useState<SelectedType>(0);

    const setTab = useCallback(
        (index: SelectedType) => {
            setSelected(index);
            if (index === 1) {
                createAnalyticsEvent({
                    eventType: "ui",
                    action: "clicked",
                    actionSubject: "tab",
                    actionSubjectId: "searchAiSummarySourcesTabClicked",
                    source: "searchAiSummary"
                }).fire();
            }
        },
        [setSelected, createAnalyticsEvent]
    );

    const statusComponent = useCallback(
        (props: { text?: string; sources?: Source[] }) => (
            <StatusComponent {...props} sources={sources} />
        ),
        [sources]
    );

    const renderMarkdownContent = (text: string) => {
        // Use the search-ai approach: encodeAdf + Atlaskit renderer
        // This handles both markdown formatting and citations properly
        return (
            <div css={markdownContainerStyle}>
                <MarkdownRenderer
                    answerString={text}
                    statusComponent={statusComponent}
                />
            </div>
        );
    };

    useEffect(() => {
        if (!contentRef.current) {
            return;
        }

        const resizeObserver = new ResizeObserver(() => {
            if (contentRef.current) {
                const wrapperHeight = contentRef.current.offsetHeight;
                const contentHeight = contentRef.current.scrollHeight;
                setIsTruncated(contentHeight > wrapperHeight);
            }
        });

        resizeObserver.observe(contentRef.current);
        return () => resizeObserver.disconnect();
    }, [aiSummary]);

    const handleToggleExpansion = () => {
        setIsExpanded(!isExpanded);
        createAnalyticsEvent({
            eventName: isExpanded
                ? "searchAiSummaryReadLessClicked"
                : "searchAiSummaryReadMoreClicked",
            eventType: "ui",
            action: "clicked",
            actionSubject: "linkButton",
            actionSubjectId: isExpanded
                ? "searchAiSummaryReadLess"
                : "searchAiSummaryReadMore",
            source: "searchAiSummary",
            attributes: {
                product: "search",
                labelEnglish: isExpanded ? "Read less" : "Read more"
            }
        }).fire();
    };

    const handleSourceLinkClick = (source: Source, index: number) => {
        createAnalyticsEvent({
            eventName: "searchAiSummarySourceLinkClicked",
            eventType: "ui",
            action: "clicked",
            actionSubject: "link",
            actionSubjectId: "searchAiSummarySourceLink",
            source: "searchAiSummary",
            attributes: {
                product: "search",
                sourceUrl: source.url,
                sourceTitle: source.title,
                sourceIndex: index
            }
        }).fire();
    };

    const readMoreText = intl.formatMessage({
        id: "ai.summary.answer.readMore",
        defaultMessage: "Read more"
    });

    return (
        <div>
            <AISummaryTabs
                answerPanel={
                    <>
                        <div
                            ref={contentRef}
                            css={[defaultContent, isExpanded && fullContent]}
                        >
                            {renderMarkdownContent(aiSummary)}
                            {isTruncated && !isExpanded && (
                                <div css={blurOverlayStyle}>
                                    {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                                    <Link
                                        onClick={handleToggleExpansion}
                                        href="#"
                                        ignoreHref={true}
                                        css={blurTextStyle}
                                        text={readMoreText}
                                        ariaLabel={readMoreText}
                                        type="anchor"
                                    />
                                </div>
                            )}
                        </div>
                    </>
                }
                sourcesPanel={sources?.map((source, i) => (
                    <div key={source.url} css={sourceStyle}>
                        <Link
                            text={i + 1 + ". " + source.title}
                            type="primary"
                            href={source.url}
                            onClick={() => handleSourceLinkClick(source, i)}
                        />
                    </div>
                ))}
                selected={selected}
                onChange={setTab}
            />
            <div css={dividerStyle} />
            <AISummaryFooter aiSummary={aiSummary} />
        </div>
    );
};
